import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp,
  Assignment,
  People,
} from '@mui/icons-material';
import { useItemStatistics } from '../../hooks/useApi';

const DashboardPage: React.FC = () => {
  const { data: statistics, isLoading, error } = useItemStatistics();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Found':
        return 'primary';
      case 'Claimed':
        return 'warning';
      case 'Returned':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Found':
        return '未引き取り';
      case 'Claimed':
        return '引き取り済み';
      case 'Returned':
        return '返却完了';
      case 'Disposed':
        return '処分済み';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          統計データの読み込みに失敗しました: {error.message}
        </Alert>
      </Container>
    );
  }

  const stats = [
    {
      title: '総落とし物数',
      value: statistics?.total_items.toLocaleString() || '0',
      icon: <Assignment sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      change: `${statistics?.recent_items || 0}件（30日間）`,
    },
    {
      title: '今月の新規登録',
      value: statistics?.recent_items.toLocaleString() || '0',
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      change: '過去30日間',
    },
    {
      title: '返却完了',
      value: statistics?.status_distribution.find(s => s.status === 'Returned')?.count.toLocaleString() || '0',
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
      change: '累計',
    },
    {
      title: '未引き取り',
      value: statistics?.status_distribution.find(s => s.status === 'Found')?.count.toLocaleString() || '0',
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
      change: '未引き取り',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        ダッシュボード
      </Typography>

      {/* 統計カード */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {stats.map((stat, index) => (
          <Card key={index} sx={{ flex: '1 1 240px', minWidth: 240 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" component="div">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: stat.color }}>
                    {stat.change}
                  </Typography>
                </Box>
                <Box sx={{ color: stat.color }}>
                  {stat.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* ステータス別分布 */}
        <Paper sx={{ p: 3, flex: '2 1 500px', minWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            ステータス別分布
          </Typography>
          <Box sx={{ mt: 2 }}>
            {statistics?.status_distribution.map((item) => (
              <Box
                key={item.status}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid #e0e0e0',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle1">{getStatusLabel(item.status)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.count}件
                  </Typography>
                </Box>
                <Chip
                  label={getStatusLabel(item.status)}
                  color={getStatusColor(item.status) as any}
                  size="small"
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* クイックアクション */}
        <Paper sx={{ p: 3, flex: '1 1 300px', minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            クイックアクション
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Typography variant="subtitle2">新しい落とし物を登録</Typography>
                <Typography variant="body2" color="text.secondary">
                  発見した落とし物を登録
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Typography variant="subtitle2">落とし物を検索</Typography>
                <Typography variant="body2" color="text.secondary">
                  条件を指定して検索
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Typography variant="subtitle2">統計レポート</Typography>
                <Typography variant="body2" color="text.secondary">
                  詳細な統計を確認
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default DashboardPage;